import {
    DefaultButton, Dialog, DialogFooter, DialogType, IconButton, MessageBar, MessageBarType, PrimaryButton, Text,
} from '@fluentui/react'
import useTranslate from 'helpers/hooks/useTranslate'
import HandleBlob from 'helpers/methods/blob'
import React, { useCallback, useRef, useState } from 'react'
import CancelRequestError from 'requests/errors/cancelRequestError'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import FilesHandler from 'requests/handlers/filesHandler'
import File from 'requests/objects/file'
import styles from 'styles/components/pages/order-quotes/[id]/files/order-quote-file-list.module.scss'
import EFileFolder from 'types/files/enums/fileFolder'
import Status from 'types/status'

/**
 * OrderQuoteFileList
 * @param {object} props Props
 * @param {boolean} props.isReadOnly isReadOnly
 * @param {object} props.lang Lang
 * @param {(fileId: File['fileId']) => void} props.removeFile removeFile
 * @param {FilesHandler} props.handler handler
 * @param {object} props.item item
 * @param {File[]} props.files files
 * @returns {JSX.Element} Element
 */
export default function OrderQuoteFileList({
    item,
    files,
    isReadOnly,
    lang,
    removeFile,
    handler,
}) {
    const { tKey, tObj } = useTranslate({ lang })

    /** Is modal visible */
    const [isModalVisible, setIsModalVisible] = useState(false)
    /** Current status of the component */
    const [status, setStatus] = useState(Status.IDLE)
    /** Error message */
    const [errorMessage, setErrorMessage] = useState('')
    /** File To Delete Id */
    const [fileToDeleteId, setFileToDeleteId] = useState(0)

    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<Blob>>} */
    const handlerGetFile = useRef(null)
    /** @type {React.MutableRefObject<import('requests/apiHandler').RequestApi<File>>} */
    const handlerRemoveFile = useRef(null)

    /**
     * Callback on download file
     */
    const onDownloadFile = useCallback(
        /**
         * @param {number} fileId Fileid
         */
        async fileId => {
            try {
                setStatus(Status.PENDING)
                setErrorMessage(null)
                handlerGetFile.current = handler.getFile(EFileFolder.OrderQuoteDocument, fileId)
                const blob = await handlerGetFile.current.fetch()
                HandleBlob.download(blob, files.find(x => x.fileId === fileId)?.name)
                setStatus(Status.RESOLVED)
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        setStatus(Status.REJECTED)
                        setErrorMessage(error ?? 'anErrorHasOccurred')
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        }, [files, handler],
    )

    /**
     * Callback on delete file
     */
    const onDeleteFile = useCallback(async () => {
        if (isReadOnly)
            return

        try {
            setStatus(Status.PENDING)
            setErrorMessage(null)
            const fileToDelete = files.find(f => f.fileId === fileToDeleteId)

            if (!fileToDelete)
                throw new Error('File not found')

            const isOrderQuote = !!item.orderQuoteId || !!fileToDelete.fileToken
            const isDraftOrderQuote = isOrderQuote && !item.orderQuoteId

            handlerRemoveFile.current = handler.removeFile(
                item.orderQuoteId || 0,
                EFileFolder.OrderQuoteDocument,
                fileToDeleteId,
                isDraftOrderQuote ? fileToDelete.fileToken : undefined,
            )

            await handlerRemoveFile.current.fetch()

            removeFile(fileToDeleteId)

            setIsModalVisible(false)
            setFileToDeleteId(0)
            setStatus(Status.RESOLVED)
        } catch (error) {
            setStatus(Status.REJECTED)
            setErrorMessage(error ?? 'anErrorHasOccurred')
            // eslint-disable-next-line no-console
            console.error(error)
        }
    }, [
        fileToDeleteId,
        handler,
        isReadOnly,
        item.orderQuoteId,
        files,
        removeFile,
    ])

    return (
        <>
            <ul className={styles['file-list']}>
                {files?.map((file, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`${file.fileId}_${index}`}>
                        <Text />
                        {file.url && (
                            <Text>
                                <a
                                    className={styles.fileName}
                                    href={file.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {file.url}
                                </a>
                            </Text>
                        )}
                        {!file.url && (
                            <Text
                                className={styles.fileName}
                                onClick={() => onDownloadFile(file.fileId)}
                            >
                                {file.name}
                            </Text>
                        )}
                        {!isReadOnly && (
                            <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => {
                                    setIsModalVisible(true)
                                    setFileToDeleteId(file.fileId)
                                }}
                                styles={{ root: { margin: '-5px 0px' } }}
                            />
                        )}
                    </li>
                ))}
            </ul>
            <Dialog
                hidden={!isModalVisible}
                onDismiss={() => {
                    setIsModalVisible(false)
                    setFileToDeleteId(0)
                }}
                dialogContentProps={{
                    type: DialogType.largeHeader,
                    title: tKey('deleteDocument'),
                    subText: tKey('deleteDocumentSub'),
                }}
                modalProps={{
                    isBlocking: true,
                }}
                maxWidth="555px"
            >
                {errorMessage
                    && (
                        <>
                            <MessageBar
                                messageBarType={MessageBarType.error}
                                isMultiline={false}
                                truncated
                                onDismiss={() => setErrorMessage(null)}
                            >
                                {(typeof errorMessage === 'string'
                                    ? tKey(/** @type {any} */(errorMessage))
                                    : tObj(errorMessage))
                                    || errorMessage?.toString()}
                            </MessageBar>
                            <br />
                        </>
                    )}
                <DialogFooter>
                    <PrimaryButton
                        onClick={onDeleteFile}
                        disabled={status === Status.PENDING}
                    >
                        {tKey('yes')}
                    </PrimaryButton>
                    <DefaultButton
                        onClick={() => {
                            setIsModalVisible(false)
                            setFileToDeleteId(0)
                        }}
                        disabled={status === Status.PENDING}
                        text={tKey('cancel')}
                    />
                </DialogFooter>
            </Dialog>
        </>
    )
}
